import { matchesQuery, SEARCH_MIN_LENGTH } from '../../libs/glossary'

interface Term {
  letter: string
  search: string
}

// Every card, letter and group asks on each keystroke, so the matches are found once per query
let cache: { terms: Term[]; query: string; matches: Set<string> } | null = null

// Alpine state for /glossary: letter filter and search over server-rendered cards.
// Cards carry data-letter and data-search; the script only toggles visibility.
export const glossaryFilter = () => ({
  query: '',
  activeLetter: 'all',
  terms: [] as Term[],

  init(this: { $root: HTMLElement; terms: Term[] }) {
    this.terms = [...this.$root.querySelectorAll<HTMLElement>('[data-term]')].map((el) => ({
      letter: el.dataset.letter ?? '',
      search: el.dataset.search ?? '',
    }))
  },

  get searching() {
    return this.query.trim().length >= SEARCH_MIN_LENGTH
  },

  // Search texts of the terms that match the query
  get matches() {
    if (cache?.terms !== this.terms || cache.query !== this.query) {
      const matches = this.terms.filter((term) => matchesQuery(term.search, this.query)).map((term) => term.search)
      cache = { terms: this.terms, query: this.query, matches: new Set(matches) }
    }
    return cache.matches
  },

  get matchCount() {
    return this.matches.size
  },

  // Search with results shows a flat list; no results falls back to the full list
  get showingResults() {
    return this.searching && this.matchCount > 0
  },

  get countLabel() {
    return `${this.matchCount} ${this.matchCount === 1 ? 'term' : 'terms'} found`
  },

  // Read out by screen readers when the search result changes
  get statusLabel() {
    if (!this.searching) return ''
    return this.showingResults ? this.countLabel : `No terms found for "${this.query.trim()}"`
  },

  showGroup(letter: string) {
    if (this.showingResults) {
      return this.terms.some((term) => term.letter === letter && this.matches.has(term.search))
    }
    if (this.searching) return true
    return this.activeLetter === 'all' || this.activeLetter === letter
  },

  showTerm(search: string) {
    return !this.showingResults || this.matches.has(search)
  },

  isLetterEnabled(letter: string) {
    if (letter === 'all') return true
    return this.terms.some((term) => term.letter === letter && (!this.showingResults || this.matches.has(term.search)))
  },

  // A search resets the selection to 'All' (see onInput), so 'All' stays pressed while searching
  isLetterPressed(letter: string) {
    return this.activeLetter === letter
  },

  selectLetter(letter: string) {
    this.query = ''
    this.activeLetter = letter
  },

  // From 3 characters the search replaces the letter filter
  onInput() {
    if (this.searching) this.activeLetter = 'all'
  },

  clear() {
    this.query = ''
    this.activeLetter = 'all'
  },
})
